import Icon, { type IconId } from '@/components/shared/Icons';
import React, { useMemo, memo } from 'react';
import type { RatingType } from '@/utils/filters/getRatings';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';

interface Props {
  rating: number;
  max?: number;
  iconType?: RatingType;
  color: string;
  screenReaderText?: string;
  locale?: Locale;
}

type IconData = {
  star: { iconId: IconId[] };
  catalunya: { iconId: IconId[] };
  diamond: { iconId: IconId[] };
};

const resolveIconType = (
  type: RatingType,
  color: string
): {
  Full: JSX.Element;
  Half: JSX.Element;
  Empty: JSX.Element;
} => {
  const iconData: IconData = {
    star: { iconId: ['star-fill', 'star-half', 'star'] },
    catalunya: { iconId: ['star-fill', 'star-half', 'star'] },
    diamond: { iconId: ['diamond-filled', 'diamond-half', 'diamond-empty'] }
  };

  const icon = iconData[type];
  const className = `h-3 w-3 ${color}`;

  return {
    Full: <Icon id={icon.iconId[0]} className={className} />,
    Half: <Icon id={icon.iconId[1]} className={className} />,
    Empty: <Icon id={icon.iconId[2]} className={className} />
  };
};

function Ratings({ rating, max = 5, iconType = 'star', color, screenReaderText, locale }: Props): JSX.Element {
  // validation to check if rating is more than maximum
  const value = rating < max ? rating : max;

  const { fullIcons, halfIcons, emptyIcons } = useMemo(() => {
    const fullCount = Math.floor(value);
    const halfCount = value % 1 > 0 ? 1 : 0;
    const remainingCount = max - Math.ceil(value);
    const emptyCount = remainingCount > 0 ? remainingCount : 0;

    return {
      fullIcons: Array(fullCount).fill(0),
      halfIcons: Array(halfCount).fill(0),
      emptyIcons: Array(emptyCount).fill(0)
    };
  }, [value, max]);

  const { Full, Half, Empty } = useMemo(() => resolveIconType(iconType, color), [iconType, color]);

  return (
    <div className="flex gap-0.5">
      {screenReaderText && locale && (
        <span className="sr-only">
          {formatMessage(screenReaderText, locale, {
            rating,
            maximum: max
          })}
        </span>
      )}
      {/* eslint-disable react/no-array-index-key */}
      {fullIcons.map((_x, index) => React.cloneElement(Full, { key: `full-${index}` }))}
      {halfIcons.map((_x, index) => React.cloneElement(Half, { key: `half-${index}` }))}
      {emptyIcons.map((_x, index) => React.cloneElement(Empty, { key: `empty-${index}` }))}
      {/* eslint-disable react/no-array-index-key */}
    </div>
  );
}

export default memo(Ratings);
